import SearchIcon from "@/assets/colorable/search.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import InputField from "@/components/Common/forms/InputField";
import Loader from "@/components/Common/kpi/Loader";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import clsxm from "@/lib/clsxm";
import {Fragment, useEffect, useState, type Attributes, type HTMLAttributes, type JSX} from "react";
import {useKeyPressEvent, useToggle} from "react-use";

export type TDropdownOption<T = Record<string, unknown>> = {
  value: string;
  label: string;
  icon?: string;
} & T;

export type TDropdownProps<T extends TDropdownOption> = {
  setSelectedOption: (option: T) => void;
  selectedOption: T | null | undefined;
  options: T[];
  className?: string;
  dropdownClassName?: string;
  dropdownWrapperClassName?: string;
  isDisabled?: boolean;
  label?: string;
  labelClassName?: string;
  containerClassName?: string;
  isSearchable?: boolean;
  onSearch?: (value: string) => void;
  isAsync?: boolean;
  isFetching?: boolean;
  queryRef?(node: Element | null): void;
  shouldUseCssDivide?: boolean;
  renderButton: ({
    selectedOption,
    isOpen,
    disabled,
  }: {
    selectedOption: T | null | undefined;
    isOpen: boolean;
    disabled: boolean;
  } & HTMLAttributes<HTMLElement>) => JSX.Element;
  renderItem: ({
    option,
    isHighlighted,
    isSelected,
  }: {
    option: T;
    isHighlighted: boolean;
    isSelected: boolean;
  } & HTMLAttributes<HTMLLIElement> &
    Attributes) => JSX.Element;
};

const ReusableDropdown = <T,>({
  options,
  selectedOption,
  setSelectedOption,
  className,
  dropdownClassName,
  dropdownWrapperClassName,
  renderButton,
  renderItem,
  isDisabled,
  label,
  labelClassName,
  containerClassName,
  isSearchable = false,
  onSearch,
  isAsync = false,
  isFetching = false,
  queryRef,
  shouldUseCssDivide = true,
}: TDropdownProps<TDropdownOption<T>>) => {
  const [isOpen, toggleOpen] = useToggle(false);
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  const close = () => {
    toggleOpen(false);
    setHighlighted(null);
  };

  useKeyPressEvent("Enter", e => {
    if (highlighted !== null) {
      setSelectedOption(options[highlighted]);
      close();
      e.preventDefault();
    }
  });

  useEffect(() => {
    onSearch?.(searchValue);
  }, [searchValue]);

  return (
    <div className={clsxm("flex w-full flex-col gap-1", containerClassName)}>
      {!!label && (
        <label className={clsxm("text-neutral-grey-dark text-sm font-medium", labelClassName)}>{label}</label>
      )}
      <Popover
        open={isOpen}
        onOpenChange={next => {
          toggleOpen(next);
          if (!next) setHighlighted(null);
        }}
        modal={false}>
        <div className={clsxm("relative flex w-full flex-col items-stretch gap-1", className)}>
          <PopoverTrigger asChild>
            {renderButton({
              selectedOption,
              isOpen,
              disabled: isDisabled ?? false,
              className:
                "duration-250 bg-white relative disabled:bg-neutral-white-dark disabled:cursor-not-allowed disabled:text-neutral-grey-dark flex h-14 grow items-center rounded-md border outline-hidden transition-all focus:border-primary-medium-solid",
            })}
          </PopoverTrigger>
        </div>
        <PopoverContent
          disableAnimation
          onOpenAutoFocus={isSearchable ? undefined : e => e.preventDefault()}
          wrapperClassName={clsxm("w-(--radix-popover-trigger-width)", dropdownWrapperClassName)}
          className="border-0 bg-transparent p-0 shadow-none">
          <ul
            className={clsxm(
              "border-neutral-grey-light text-neutral-black mb-0 max-h-(--radix-popover-content-available-height) w-full overflow-auto rounded-md border bg-white pl-0 shadow-lg",
              shouldUseCssDivide ? "divide-neutral-grey-light divide-y" : "",
              dropdownClassName,
            )}>
            {isSearchable && !!onSearch && (
              <div className="sticky top-0 bg-white p-5 pb-2.5">
                <InputField
                  onChange={value => setSearchValue(value)}
                  className="border-primary-light-solid relative h-auto rounded-full border px-10 py-2.5"
                  value={searchValue}
                  icon={
                    <>
                      <SearchIcon className="text-neutral-grey-light absolute top-1/2 left-4 size-4 -translate-y-1/2" />
                      {!!searchValue && (
                        <TimesIcon
                          onClick={() => setSearchValue("")}
                          className="text-neutral-grey-light absolute top-1/2 right-4 size-3 shrink-0 -translate-y-1/2 cursor-pointer"
                        />
                      )}
                    </>
                  }
                />
              </div>
            )}
            {options.map((option, index, array) => (
              <Fragment key={index}>
                {renderItem({
                  option,
                  isHighlighted: index === highlighted,
                  isSelected: option.value === selectedOption?.value,
                  onClick: e => {
                    e.preventDefault();
                    close();
                    setSelectedOption(option);
                  },
                  className:
                    "duration-250 flex h-14 cursor-pointer items-center justify-start gap-2 px-4 py-2.5 text-left transition-all",
                  key: index,
                })}
                {isAsync && index === array.length - 1 && <div ref={queryRef} className="h-1" />}
              </Fragment>
            ))}
            {isFetching && <Loader styles={{containerClassName: "py-5 shrink-0"}} />}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ReusableDropdown;
